import { useQuery } from '@apollo/client'
import { useState } from 'react'

import { usePrevious } from 'hooks/usePrevious'
import { DEFAULT_POOLING_INTERVAL } from 'providers/graphql/constants'
import { isArrayEqualByKeys } from 'utils/array'
import { fromDTO, GET_BUILD_ORDERS, sortToDTO } from './api'
import { BuildOrderForTable, UseGetBuildOrders, UseGetBuildOrdersProps } from './types'

export const useGetBuildOrders = ({
  filters,
  first,
  last,
  sort,
}: UseGetBuildOrdersProps): UseGetBuildOrders => {
  const [buildOrders, setBuildOrders] = useState<BuildOrderForTable[]>([])
  const [dataCount, setDataCount] = useState<number>(0)
  const [poolCount, setPoolCount] = useState<number>(0)

  const options: string = JSON.stringify({ filters, first, last, sort })
  const prevOptions = usePrevious<string>(options)

  const { loading } = useQuery(GET_BUILD_ORDERS, {
    onCompleted: ({ buildOrders: { nodes, totalCount } }): void => {
      if (nodes) {
        const parsedNodes: BuildOrderForTable[] = nodes.map(fromDTO)

        const update = (): void => {
          setBuildOrders(parsedNodes)
          setDataCount(totalCount)
        }

        // Only update data on initial load, query changes, or relevant field changes to minimize re-renders that cause DiffViewer to redraw.
        if (poolCount === 0 || options !== prevOptions) {
          update()
        } else if (poolCount >= 1) {
          const shouldUpdate = !isArrayEqualByKeys<BuildOrderForTable>({
            keys: ['changes', 'status'],
            next: parsedNodes,
            prev: buildOrders,
          })

          if (shouldUpdate) {
            update()
          }
        }
      }

      setPoolCount(prev => prev + 1)
    },
    pollInterval: DEFAULT_POOLING_INTERVAL,

    variables: {
      first,
      last,
      sort: sortToDTO(sort),
      terms: filters,
    },
  })

  return {
    buildOrders,
    count: dataCount,
    loading,
  }
}
